/**
 * Quote Detail page tour — /operator/newQuotePage/:id (and admin/*).
 *
 * Walks an operator through the end-to-end workflow:
 *   query arrives → AI parses → rates → cost table → finalise → send to customer.
 *
 * Anchors (all verified present in code):
 *   qd-header              Top bar with status, route, ID
 *   qd-stage-bar           Stage progression tracker
 *   qd-workspace-controls  Checklist + quick actions
 *   qd-right-panel         Shipment inspector (right column)
 *   qd-workspace-lanes     Four lane switcher (left column)
 *   qd-customer-lane       Customer lane button
 *   qd-airline-lane        Airline lane button
 *   qd-transport-lane      Transporter lane button
 *   qd-cost-lane           Final cost table lane button
 *   qd-conversation        Active lane content (centre column)
 *   airline-section-tabs   Charges / Messages toggle inside airline
 *   airline-rates          Agreed-rates editor inside airline
 *   transport-section-tabs Charges / Messages toggle inside transporter
 *   transport-rates        Agreed-rates editor inside transporter
 *   qd-reply-composer      Reply strip inside the active thread
 *   qd-cost-table          Final consolidated cost table
 *
 * Media (optional):
 *   Drop animated GIFs at public/walkthrough/quote-detail/step-<N>.gif and
 *   they appear above each step's description. See public/walkthrough/README.md
 *   for the recommended recording workflow. If a file is missing, the step
 *   still renders cleanly — the img element hides itself on load error.
 */

const M = (step, alt, caption) => ({
  src: `/walkthrough/quote-detail/step-${step}.gif`,
  alt,
  caption,
});

export const quoteDetailTour = {
  id: "quote-detail",
  label: "Quote Workspace — Query to Nomination",
  steps: [
    // 0 — Orient
    {
      target: "qd-header",
      title: "Welcome to the quote workspace",
      description:
        "Everything for this shipment lives on one page — the route, the stage, every conversation, and the final cost table. We'll walk through the full path from a new query to a customer-ready quote. About 60 seconds.",
      bullets: [
        "Top bar shows status, type, quote ID and route",
        "Use the stage bar below to track progress",
        "Press 'Skip' anytime if you already know your way around",
      ],
      placement: "bottom",
      media: M(0, "Quote workspace overview", "The page you'll spend most of your time on."),
    },

    // 1 — Stages
    {
      target: "qd-stage-bar",
      title: "Track the stage at the top",
      description:
        "Every quote moves through four stages. The system advances stages automatically when you complete the right tasks — you don't have to set them manually.",
      bullets: [
        "Query — enquiry just arrived",
        "Quote — rates gathered and table ready",
        "Nomination — customer has picked their option",
        "Collection — cargo is on the way",
      ],
      placement: "bottom",
      media: M(1, "Stage tracker", "Each stage lights up as you complete its checklist."),
    },

    // 2 — Checklist + actions
    {
      target: "qd-workspace-controls",
      title: "Your checklist + quick actions",
      description:
        "Click the checklist to see exactly what's needed to advance the stage. The shortcuts row also opens the freight calculator and other tools without leaving the page.",
      hint: "If an action is greyed out, the data it needs isn't ready yet — usually a missing field in the inspector on the right.",
      placement: "left",
      media: M(2, "Checklist and quick actions"),
    },

    // 3 — Inspector
    {
      target: "qd-right-panel",
      title: "Always read the shipment first",
      description:
        "The right panel shows the structured cargo: origin, destination, packages, weights, customer/shipper details. Read this before you talk to anyone — it's faster than scrolling the email.",
      bullets: [
        "Fields tagged 'AI' were auto-filled from the original email",
        "Click 'Re-parse' to re-run AI if the email was edited",
        "Click 'Request Missing' to ping the customer for blanks",
      ],
      placement: "left",
      media: M(3, "Shipment inspector", "AI tags show which fields the model extracted."),
    },

    // 4 — The four lanes
    {
      target: "qd-workspace-lanes",
      title: "Four lanes — one for each conversation",
      description:
        "Every quote has up to four parallel conversations. You switch lanes any time without losing context. The currently active lane lights up.",
      bullets: [
        "Customer — the original enquiry thread",
        "Airline — collect carrier rates",
        "Transporter — collect trucking rates",
        "Shipper — coordinate cargo pickup",
      ],
      placement: "right",
      media: M(4, "Four-lane switcher"),
    },

    // 5 — Customer lane
    {
      target: "qd-customer-lane",
      title: "Step 1 — read the customer enquiry",
      description:
        "Open the customer lane to read what the customer asked for and check any attachments. Understand the brief before sourcing rates.",
      placement: "right",
      media: M(5, "Open customer lane", "Click 'Customer' on the left rail."),
    },

    // 6 — Reply composer
    {
      target: "qd-reply-composer",
      title: "Reply to the customer here",
      description:
        "Compose your reply, attach files, or pick a saved template. Send Reply only enables when there's typed text or an attachment — empty emails can't go out by accident.",
      bullets: [
        "Use the AI Assist (✨) for a suggested reply draft",
        "Pick a template (NS:/QS:) from the dropdown for boilerplate",
        "Attachments are sent inline with the email",
      ],
      hint: "The composer is collapsed by default — click 'Reply' to expand it.",
      interactive: true,
      placement: "top",
      media: M(6, "Reply composer", "Type, attach, pick a template, send."),
    },

    // 7 — Airline lane
    {
      target: "qd-airline-lane",
      title: "Step 2 — collect airline rates",
      description:
        "Switch to the airline lane. This is where you talk to carriers and capture every quote you receive.",
      placement: "right",
      media: M(7, "Open airline lane"),
    },

    // 8 — Section tabs
    {
      target: "airline-section-tabs",
      title: "Two tabs in every partner lane",
      description:
        "Every airline / transporter / shipper lane has the same two tabs: 'Charges & details' (the rates) and 'Messages' (the email thread). Flip between them freely.",
      bullets: [
        "Charges — where you enter the agreed rate",
        "Messages — where you negotiate it via email",
      ],
      placement: "bottom",
      media: M(8, "Charges vs Messages tabs"),
    },

    // 9 — Airline rates
    {
      target: "airline-rates",
      title: "Enter the agreed airline rate",
      description:
        "When the carrier confirms a price, capture it here exactly as agreed. These numbers feed directly into the final cost table.",
      bullets: [
        "Save only enables when Final Rate > 0",
        "Use the AI Rate Extractor tab to parse rates from a pasted email",
        "Add multiple destination airports — each becomes a row in the cost table",
      ],
      hint: "Not in your airline list? Type the airline name and save — it's created on the fly.",
      interactive: true,
      placement: "top",
      media: M(9, "Airline rate editor"),
    },

    // 10 — Transporter lane
    {
      target: "qd-transport-lane",
      title: "Step 3 — transporter rates",
      description:
        "Same pattern on the trucking side: pick or create a transporter, agree a rate, save it.",
      hint: "If the transporter isn't in your directory, just type a name and save — added instantly.",
      placement: "right",
      media: M(10, "Open transporter lane"),
    },

    // 11 — Transport rates
    {
      target: "transport-rates",
      title: "Enter the transporter's rate",
      description:
        "Capture the truck rate and screening prices the same way as the airline side.",
      bullets: [
        "Per-destination charges go in one block each",
        "Screening prices (primary + secondary) are optional but recommended",
      ],
      interactive: true,
      placement: "top",
      media: M(11, "Transporter rate editor"),
    },

    // 12 — Final cost lane
    {
      target: "qd-cost-lane",
      title: "Step 4 — build the final cost table",
      description:
        "The final cost table combines every airline × transporter combination. Adjust margin per row, then either finalise one option or nominate several for the customer to pick.",
      placement: "right",
      media: M(12, "Open the final cost table"),
    },

    // 13 — Cost table
    {
      target: "qd-cost-table",
      title: "Nominate or finalise the winning option",
      description:
        "Inside the cost table modal you'll see every combination. Use the 'Preview as customer' pill at the top right to hide internal columns (margin, customs, extras) while reviewing. Once you've picked the winner, click Finalise.",
      bullets: [
        "Margin / Customs / Extra are editable per row",
        "Customer preview toggle hides internal numbers",
        "After finalising, use 'Send to Customer Conversation' to attach the customer-safe table to the customer thread and auto-fill the Nomination Confirmation template",
        "Or use 'Copy for email' to paste into Gmail / Outlook directly",
      ],
      placement: "top",
      media: M(13, "Cost table → finalise → send", "Full workflow from combinations to customer email."),
    },
  ],
};
